<ng-container *transloco="let t">
  <app-topbar-popover #popover="appTopbarPopover" [ariaLabel]="t('weather.details')">
    <!-- Trigger: styled like the toolbar's plain read-outs (no button chrome),
         deliberately understated. Shows the app-wide dash until the first
         reading lands. -->
    <button
      type="button"
      (click)="popover.toggle()"
      class="flex cursor-pointer items-center gap-2"
      aria-haspopup="dialog"
      [attr.aria-expanded]="popover.isOpen()"
      [attr.aria-label]="t('weather.details')"
    >
      <app-icon [name]="icon()" [size]="24" />
      <span class="text-body-1 whitespace-nowrap">
        {{ weather.current()?.tempC | measure: 'temperature' }}
      </span>
    </button>

    <ng-template appPopoverPanel>
      @if (weather.current(); as w) {
        <!-- Condition + temperature header -->
        <div class="flex items-start justify-between gap-4">
          <div class="flex min-w-0 items-center gap-3.5">
            <app-icon class="shrink-0" [name]="icon()" [size]="44" />
            <div class="min-w-0">
              <div class="font-display text-base font-semibold first-letter:uppercase">
                {{ w.description }}
              </div>
              <div class="mt-0.5 text-caption text-ink-2">
                {{ w.city }}, {{ w.country }} · {{ w.conditionId }} {{ w.group }}
              </div>
            </div>
          </div>
          <div class="shrink-0 text-right">
            <div class="font-display text-[28px] font-semibold leading-8">
              {{ w.tempC | measure: 'temperature' }}
            </div>
            <div class="mt-0.5 text-caption text-ink-2">
              {{ t('weather.feelsLike', { temp: (w.feelsLikeC | measure: 'temperature') }) }}
            </div>
          </div>
        </div>

        <div class="my-4 h-px bg-hairline"></div>

        <!-- Detail grid -->
        <div class="grid grid-cols-2 gap-x-4 gap-y-3.5">
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.humidity') }}</div>
            <div class="mt-0.5 text-sm font-semibold">{{ w.humidityPct }}%</div>
          </div>
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.wind') }}</div>
            <div class="mt-0.5 text-sm font-semibold">
              {{ w.windKmh | measure: 'speedKmh' }}
              @if (compassKey(); as key) {
                {{ t('weather.compass.' + key) }}
              }
            </div>
          </div>
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.gusts') }}</div>
            <div class="mt-0.5 text-sm font-semibold">{{ w.gustKmh | measure: 'speedKmh' }}</div>
          </div>
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.pressure') }}</div>
            <div class="mt-0.5 text-sm font-semibold">
              {{ t('weather.pressureValue', { value: w.pressureHpa }) }}
            </div>
          </div>
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.visibility') }}</div>
            <div class="mt-0.5 text-sm font-semibold">
              {{ w.visibilityKm | measure: 'distanceKm' }}
            </div>
          </div>
          <div>
            <div class="text-caption text-ink-3">{{ t('weather.clouds') }}</div>
            <div class="mt-0.5 text-sm font-semibold">{{ w.cloudsPct }}%</div>
          </div>
          @if (w.rainMm !== null) {
            <div>
              <div class="text-caption text-ink-3">{{ t('weather.rainLastHour') }}</div>
              <div class="mt-0.5 text-sm font-semibold">{{ w.rainMm | measure: 'lengthMm' }}</div>
            </div>
          }
          @if (w.snowMm !== null) {
            <div>
              <div class="text-caption text-ink-3">{{ t('weather.snowLastHour') }}</div>
              <div class="mt-0.5 text-sm font-semibold">{{ w.snowMm | measure: 'lengthMm' }}</div>
            </div>
          }
        </div>

        <div class="mb-3 mt-4 h-px bg-hairline"></div>

        <!-- Sun times + data freshness -->
        <div class="flex items-center justify-between gap-3 text-caption text-ink-2">
          <span>{{ t('weather.sunrise', { time: sunrise() }) }} · {{ t('weather.sunset', { time: sunset() }) }}</span>
          <span>
            @if (minutesAgo() === 0) {
              {{ t('weather.updatedJustNow') }}
            } @else {
              {{ t('weather.updatedMinutesAgo', { minutes: minutesAgo() }) }}
            }
          </span>
        </div>
      } @else {
        <div class="text-sm text-ink-2">
          {{ weather.failed() ? t('weather.unavailable') : t('weather.loading') }}
        </div>
      }
    </ng-template>
  </app-topbar-popover>
</ng-container>
